import { useMemo, useState } from "react";
import { STOCK_LIBRARY, STOCK_TAGS } from "./stockLibrary";
import { X, Search, Image as ImageIcon } from "lucide-react";

/**
 * StockPicker — modal that lets the admin pick a free Unsplash image
 * for any block requiring an image URL.
 *
 * Usage:
 *   <StockPicker onSelect={url => setImage(url)} onClose={() => setOpen(false)} />
 */
export function StockPicker({ onSelect, onClose }) {
  const [tag, setTag] = useState("all");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return STOCK_LIBRARY.filter((img) => {
      if (tag !== "all" && !img.tags.includes(tag)) return false;
      if (qq && !(img.alt.toLowerCase().includes(qq) || img.tags.join(" ").includes(qq))) return false;
      return true;
    });
  }, [tag, q]);

  return (
    <div className="fixed inset-0 z-[1000] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white border-2 border-slate-900 w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-900/15">
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5 text-[#FFC107]"/>
            <h2 className="font-display font-bold text-xl">Galerie d'images libres</h2>
            <span className="text-xs text-slate-500 font-mono">{filtered.length} résultats</span>
          </div>
          <button onClick={onClose} className="p-2 border border-slate-900 hover:bg-red-600 hover:text-white hover:border-red-600" aria-label="Fermer"><X className="w-4 h-4"/></button>
        </div>

        {/* Filters */}
        <div className="p-5 border-b border-slate-900/15 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher (entrepôt, solaire, drone…)"
              className="input pl-10 w-full"
              autoFocus
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {STOCK_TAGS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTag(t.value)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${tag === t.value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {filtered.length === 0 ? (
            <div className="text-center text-slate-500 py-16 font-mono text-sm">Aucune image trouvée. Essaie une autre catégorie ou un autre mot-clé.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => { onSelect(img.url, img.alt); onClose(); }}
                  className="group relative aspect-[4/3] overflow-hidden border border-slate-900/20 hover:border-slate-900 transition-all bg-slate-100"
                  title={`${img.alt} — © ${img.author} (Unsplash)`}
                >
                  <img src={img.thumb} alt={img.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"/>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end p-2">
                    <span className="text-white text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity truncate">© {img.author}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer credit */}
        <div className="p-3 border-t border-slate-900/10 text-xs text-slate-500 font-mono">
          Images libres de droits — <a href="https://unsplash.com/license" target="_blank" rel="noreferrer" className="underline hover:text-slate-900">Licence Unsplash</a>. Utilisation commerciale autorisée.
        </div>
      </div>
    </div>
  );
}
